import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import FileSaver from 'file-saver';
import { forkJoin, from } from 'rxjs';
import { BusinessComplianceSettingForm } from 'src/app/classes/form-groups/business-compliance-setting-form';
import { ComplianceFindingForm } from 'src/app/classes/form-groups/compliance-finding-form';
import {
  BusinessVerificationApiError,
  ClientRegisteredVendor,
  Company,
  CompanyVendor,
  CompanyVendorFile,
  File as FileModel,
  Note,
  RequestJob,
  User
} from 'src/app/classes/models';
import { BusinessComplianceSetting } from 'src/app/classes/models/business-compliance-setting';
import { ComplianceFinding } from 'src/app/classes/models/compliance-finding';
import { ComplianceResultStatuses } from 'src/app/interfaces/compliance/compliance-result-statuses';
import { NewBusinessInfoComplianceResults } from 'src/app/interfaces/compliance/results/business-info/new-business-info-compliance-results';
import { LocalizationInfo } from 'src/app/interfaces/localization-info';
import { ConfirmModalComponent } from 'src/app/modals/confirm-modal/confirm-modal.component';
import { NoteModalComponent } from 'src/app/modals/note-modal/note-modal.component';
import { AuthService } from 'src/app/services/auth.service';
import { BusinessVerificationApiErrorsService } from 'src/app/services/business-verification-api-errors.service';
import { CompanyVendorService } from 'src/app/services/company-vendor.service';
import { ComplianceService } from 'src/app/services/compliance.service';
import { ReportService } from 'src/app/services/report.service';
import { RequestJobService } from 'src/app/services/request-job.service';
import { ToastService } from 'src/app/services/toast.service';
import { UtilityService } from 'src/app/services/utility.service';
import { businessVerificationApiErrorsService } from 'src/app/shared/vive-api/business-verification-api-errors.service';
import { LOAD_BALANCER_TIMEOUT_MS } from 'src/app/shared/vive-api/vive-api.config';

declare type ComplianceResultsToFindingsMap = {
  addressResults: 'companyAddress';
  bankruptcyResults: 'companyBankruptcy';
  businessVerifiedResults: 'companyBusinessVerified';
  governmentWatchlistResults: 'companyGovernmentWatchlist';
  lienResults: 'companyLiensAndJudgments';
  requesterResults: 'companyRequester';
  secretaryOfStateResults: 'companySecretaryOfState';
  w9Results: 'companyW9Check';
};

@Component({
  selector: 'app-client-registered-vendor-compliance',
  templateUrl: './client-registered-vendor-compliance.component.html',
  styleUrl: './client-registered-vendor-compliance.component.less',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class ClientRegisteredVendorComplianceComponent implements OnInit {
  constructor(
    private CompanyVendorService: CompanyVendorService,
    private ToastService: ToastService,
    private ComplianceService: ComplianceService,
    private Route: ActivatedRoute,
    private UtilityService: UtilityService,
    private ModalService: NgbModal,
    private AuthService: AuthService,
    private RequestJobService: RequestJobService,
    private ReportService: ReportService,
    private BusinessVerificationApiErrorService: BusinessVerificationApiErrorsService,
    private cdr: ChangeDetectorRef
  ) {
    this.LoggedInUser = this.AuthService.getUser();
  }

  @Input() CompanyVendor?: CompanyVendor;
  @Output() companyVendorChanged = new EventEmitter<CompanyVendor>();

  Company?: Company;
  ClientRegisteredVendor?: ClientRegisteredVendor;

  LoggedInUser: User;

  BusinessComplianceSettingForm: BusinessComplianceSettingForm =
    new BusinessComplianceSettingForm();
  ComplianceFindingForm: ComplianceFindingForm = new ComplianceFindingForm();
  BusinessComplianceSetting?: BusinessComplianceSetting;
  ComplianceFinding?: ComplianceFinding;
  BusinessComplianceResults?: NewBusinessInfoComplianceResults;
  LocalizationInfo!: LocalizationInfo;
  isRunningBusinessVerification: boolean = false;
  hasBusinessReportData: boolean = false;
  RequestJobs: RequestJob[] = [];
  BusinessVerificationApiErrors: BusinessVerificationApiError[] = [];
  BusinessVerificationApiErrorLongestRetryTime?: BusinessVerificationApiError;

  ComplianceResultsToFindingsMap: ComplianceResultsToFindingsMap = {
    addressResults: 'companyAddress',
    bankruptcyResults: 'companyBankruptcy',
    businessVerifiedResults: 'companyBusinessVerified',
    governmentWatchlistResults: 'companyGovernmentWatchlist',
    lienResults: 'companyLiensAndJudgments',
    requesterResults: 'companyRequester',
    secretaryOfStateResults: 'companySecretaryOfState',
    w9Results: 'companyW9Check'
  };

  // Used to periodically check if a request job is finished processing.
  RequestJobTimer?: any;

  get nextRunInHours(): string | null {
    const nextRun =
      this.BusinessVerificationApiErrorLongestRetryTime?.nextRunDate;

    if (!nextRun) {
      return null;
    }

    const now = new Date();
    const runDate = new Date(nextRun);
    const diffMs = runDate.getTime() - now.getTime();
    const diffHours = Math.ceil(diffMs / (1000 * 60 * 60));

    return diffHours > 0
      ? `${diffHours} hour${diffHours === 1 ? '' : 's'} from now`
      : 'less than 1 hour from now';
  }

  get hasLexisNexisCorporateFilingError(): boolean {
    const serverErrorPattern = /status code 5\d{2}/;

    return this.BusinessVerificationApiErrors.some((error) => {
      if (error.apiType !== 'Lexis Nexis Corporate Filing') {
        return false;
      }

      const errorJson = JSON.stringify(error.errorResponseJSON || '');
      return serverErrorPattern.test(errorJson);
    });
  }

  yesNoValues: { label: string; value: boolean | null }[] = [
    { label: '', value: null },
    { label: 'No', value: false },
    { label: 'Yes', value: true }
  ];

  watchlistValues: { label: string; value: number | null }[] = [
    { label: '', value: null },
    { label: 'No Watchlists', value: 0 },
    { label: 'Watchlist Found', value: 1 }
  ];

  bankruptcyValues: { label: string; value: number | null }[] = [
    { label: '', value: null },
    { label: 'No Bankruptcies', value: 0 },
    { label: 'Forgiven Bankruptcies', value: -1 },
    { label: '0 - 1 Year Post Decisions', value: 1 },
    { label: '2 Years Post Decisions', value: 2 },
    { label: '3 Years Post Decisions', value: 3 },
    { label: '4 Years Post Decisions', value: 4 },
    { label: '5 Years Post Decisions', value: 5 },
    { label: '6 Years Post Decisions', value: 6 },
    { label: '7 Years Post Decisions', value: 7 },
    { label: '8+ Years Post Decisions', value: 8 }
  ];

  addressValues: { label: string; value: string | null }[] = [
    { label: '', value: null },
    { label: 'Verified', value: 'Verified' },
    { label: 'Not Matching', value: 'Not Matching' }
  ];

  secretaryOfStateValues: { label: string; value: string | null }[] = [
    { label: '', value: null },
    { label: 'Not Required', value: 'Not Required' },
    { label: 'In Good Standing', value: 'In Good Standing' },
    { label: 'Inactive', value: 'Inactive' },
    { label: 'Not In Good Standing', value: 'Not In Good Standing' },
    { label: 'Manual Review Needed', value: 'Manual Review Needed' },
    { label: 'No Records Found', value: 'No Records Found' }
  ];

  requesterValues: { label: string; value: string | null }[] = [
    { label: '', value: null },
    { label: 'Likely Matching', value: 'Likely Matching' },
    { label: 'Not Matching', value: 'Not Matching' }
  ];

  hasActiveBusinessVerificationApiError() {
    return !!this.BusinessVerificationApiErrors.find(
      (bvae) => !bvae.isArchived
    );
  }

  clearBusinessVerificationErrors() {
    const { componentInstance, result } = this.ModalService.open(
      ConfirmModalComponent,
      { centered: true, size: 'md' }
    );

    Object.assign(componentInstance, {
      displayText:
        'This will remove all business verification errors. Do you wish to continue?'
    });

    from(result).subscribe({
      next: (confirm) => {
        if (confirm) {
          from(
            businessVerificationApiErrorsService.archiveAllForVendor({
              data: {
                ClientRegisteredVendorId: this.ClientRegisteredVendor?.id
              },
              options: { requestTimeout: LOAD_BALANCER_TIMEOUT_MS }
            })
          ).subscribe({
            next: () => {
              if (this.BusinessVerificationApiErrors.length > 0) {
                this.BusinessVerificationApiErrors.forEach((bvae) =>
                  Object.assign(bvae, { isArchived: true })
                );
              }
              this.ToastService.addSuccessToast(
                'Success',
                'Successfully removed business verification errors.'
              );
            }
          });
        }
      }
    });
  }

  doesResultsHaveFailedOrMissing() {
    return (
      this.BusinessComplianceResults &&
      !!Object.keys(this.BusinessComplianceResults).find((field) => {
        const value =
          this.BusinessComplianceResults![
            field as keyof NewBusinessInfoComplianceResults
          ];

        return new Set([
          'FAILED',
          'NO EXACT MATCH',
          'REVIEW NEEDED',
          'MISSING'
        ]).has(value);
      })
    );
  }

  // If the logged in user is a VIVE Admin or the user is a Client with failed findings,
  // allow the findings to be overridden.
  areFindingsOverrideable() {
    if (!this.LoggedInUser) {
      return false;
    }

    const { UserAccountType, UserAccountRole, isSuperUser } = this.LoggedInUser;

    if (!UserAccountType || !UserAccountRole) {
      return false;
    }

    const { role } = UserAccountRole;
    const { name } = UserAccountType;

    return (
      name === 'Admin' ||
      (role === 'CLIENT_ADMIN' &&
        isSuperUser &&
        this.doesResultsHaveFailedOrMissing())
    );
  }

  getWatchlistRequirementLabel(watchlistNumber: number | null) {
    const watchlistToLabelMap: { [key: number | string]: string } = {
      null: '',
      0: 'No Watchlists',
      1: '1 Watchlist',
      2: 'Multiple Watchlists'
    };

    if (watchlistNumber === null) {
      return watchlistToLabelMap['null'];
    }

    return watchlistToLabelMap[watchlistNumber];
  }

  getBankruptcyRequirementLabel(numberOfYears: number) {
    const yearToLabelMap: { [key: number]: string } = {
      0: 'No Bankruptcies',
      [-1]: 'Forgiven Bankruptcies',
      1: '0 - 1 Year Post Decisions',
      2: '2 Years Post Decisions',
      3: '3 Years Post Decisions',
      4: '4 Years Post Decisions',
      5: '5 Years Post Decisions',
      6: '6 Years Post Decisions',
      7: '7 Years Post Decisions',
      8: '8+ Years Post Decisions'
    };

    return yearToLabelMap[numberOfYears];
  }

  checkIfNullOrUndefined(value: any) {
    return ['null', 'undefined'].includes(typeof value);
  }

  isNotRequiredResult(
    result: ComplianceResultStatuses | 'NO EXACT MATCH' | 'REVIEW NEEDED'
  ) {
    return result === 'NOT REQUIRED';
  }

  getCorporateLiensLabel(lienNumber: number) {
    return this.LocalizationInfo.moneySymbol.label.concat(
      lienNumber.toString()
    );
  }

  runBusinessVerification() {
    this.isRunningBusinessVerification = true;

    if (!this.Company || !this.ClientRegisteredVendor) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Cannot run business verification. Missing required Client/Vendor info.',
        { returnError: true }
      );
    }

    const { id: CompanyId } = this.Company;
    const { id: ClientRegisteredVendorId } = this.ClientRegisteredVendor;

    this.ComplianceService.runBusinessVerificationOnVendor(
      CompanyId!,
      { ClientRegisteredVendorId },
      { waitForJobs: true }
    ).subscribe({
      next: ([Vendor, ComplianceResults]) => {
        const { hasBusinessReportData, ComplianceFinding } = Vendor;

        if (hasBusinessReportData === undefined) {
          throw this.ToastService.addErrorToast(
            'Error',
            'There was an error loading Vendor data.',
            { returnError: true }
          );
        }

        this.hasBusinessReportData = hasBusinessReportData;

        if (ComplianceFinding) {
          this.ComplianceFinding = ComplianceFinding;
          this.ComplianceFindingForm.patchValue(ComplianceFinding);
        }

        this.loadCompanyVendorData();
        this.isRunningBusinessVerification = false;
        this.cdr.markForCheck();
      },
      error: () => {
        this.isRunningBusinessVerification = false;
        this.cdr.markForCheck();
      }
    });
  }

  downloadBusinessVerificationReport() {
    if (!this.Company || !this.ClientRegisteredVendor) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Cannot override compliance findings. Missing required Client/Vendor info.',
        { returnError: true }
      );
    }

    const { id: CompanyId } = this.Company;
    const { id: ClientRegisteredVendorId, legalName } =
      this.ClientRegisteredVendor;

    this.ReportService.generateBusinessVerificationReport({
      CompanyId,
      ClientRegisteredVendorId
    }).subscribe({
      next: (ReportData) => {
        FileSaver.saveAs(
          new Blob([ReportData]),
          `${legalName} Business Verification Report.pdf`
        );
      }
    });
  }

  downloadRawDataReports() {
    if (!this.ClientRegisteredVendor) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Cannot download raw data report. Missing required vendor info.',
        { returnError: true }
      );
    }

    const { id: ClientRegisteredVendorId, legalName } =
      this.ClientRegisteredVendor;

    forkJoin([
      this.ReportService.getLexNexReport({
        options: JSON.stringify({ limit: 1 }),
        ClientRegisteredVendorId,
        reqType: 'BusinessInstantID2',
        order: JSON.stringify([['createdAt', 'DESC']])
      }),
      this.ReportService.getLexNexReport({
        options: JSON.stringify({ limit: 1 }),
        ClientRegisteredVendorId,
        reqType: 'CorporateSearch2Request',
        order: JSON.stringify([['createdAt', 'DESC']])
      }),
      this.ReportService.getLexNexReport({
        options: JSON.stringify({ limit: 1 }),
        ClientRegisteredVendorId,
        reqType: 'CorporateReport2Request',
        order: JSON.stringify([['createdAt', 'DESC']])
      }),
      this.ReportService.getIRSReport({
        options: JSON.stringify({ limit: 1 }),
        ClientRegisteredVendorId,
        requestType: 'Tin Match',
        order: JSON.stringify([['createdAt', 'DESC']])
      }),
      this.ReportService.getTinCheckReport({
        options: JSON.stringify({ limit: 1 }),
        ClientRegisteredVendorId,
        requestType: 'ValidateTinNameAddressListMatch',
        order: JSON.stringify([['createdAt', 'DESC']])
      })
    ]).subscribe({
      next: ([
        LexNexReportInstantId2,
        LexNexReportCorporateSearch,
        LexNexReportCorporateReport,
        IRSReport,
        TinCheckReport
      ]) => {
        if (IRSReport) {
          const { responseJSON } = IRSReport;
          if (!responseJSON) {
            throw this.ToastService.addErrorToast(
              'Error',
              'Missing response data from IRS Report.',
              { returnError: true }
            );
          }
          const formattedReportData = JSON.stringify(responseJSON, null, 2);
          FileSaver.saveAs(
            new Blob([formattedReportData], { type: 'text/plain' }),
            `${legalName}-IRSRawData.txt`
          );
        }
        if (TinCheckReport) {
          const { responseData } = TinCheckReport;
          if (!responseData) {
            throw this.ToastService.addErrorToast(
              'Error',
              'Missing response data from Tin Check report.',
              { returnError: true }
            );
          }
          // Hack, use JSON parse to parse XML data into formatted string.
          let formattedReportData = JSON.stringify(responseData).replace(
            /\\r\\n/g,
            '\r\n'
          );
          formattedReportData = formattedReportData.slice(
            1,
            formattedReportData.length - 1
          );
          FileSaver.saveAs(
            new Blob([formattedReportData], { type: 'text/plain' }),
            `${legalName}-TinCheckRawData.txt`
          );
        }
        if (LexNexReportInstantId2) {
          const { resJson } = LexNexReportInstantId2;
          const formattedReportData = JSON.stringify(resJson, null, 2);
          FileSaver.saveAs(
            new Blob([formattedReportData], { type: 'text/plain' }),
            `${legalName}-LexNexInstantID2RawData.txt`
          );
        }
        if (LexNexReportCorporateSearch) {
          const { resJson } = LexNexReportCorporateSearch;
          const formattedReportData = JSON.stringify(resJson, null, 2);
          FileSaver.saveAs(
            new Blob([formattedReportData], { type: 'text/plain' }),
            `${legalName}-LexNexReportCorporateSearchRawData.txt`
          );
        }
        if (LexNexReportCorporateReport) {
          const { resJson } = LexNexReportCorporateReport;
          const formattedReportData = JSON.stringify(resJson, null, 2);
          FileSaver.saveAs(
            new Blob([formattedReportData], { type: 'text/plain' }),
            `${legalName}-LexNexReportCorporateReportRawData.txt`
          );
        }
      }
    });
  }

  getProcessingRequestJobs() {
    if (this.ClientRegisteredVendor) {
      const { id: ClientRegisteredVendorId } = this.ClientRegisteredVendor;
      this.RequestJobService.getRequestJobs(
        'data',
        this.UtilityService.stringifyObjectsInQueryObject({
          status: 'PROCESSING',
          RequestJobLookup: {
            options: { required: true },
            TableName: 'ClientRegisteredVendors',
            TableId: ClientRegisteredVendorId
          }
        })
      ).subscribe({
        next: (RequestJobs) => {
          this.RequestJobs = RequestJobs;

          if (this.RequestJobs.length > 0 && !this.RequestJobTimer) {
            this.RequestJobTimer = setInterval(
              () => this.getProcessingRequestJobs(),
              10000
            );
          } else if (this.RequestJobs.length === 0 && this.RequestJobTimer) {
            clearInterval(this.RequestJobTimer);
            this.RequestJobTimer = undefined;
            this.loadCompanyVendorData();
          }
          this.cdr.markForCheck();
        }
      });
    }
  }

  resetStuckVerification() {
    if (this.RequestJobs.length === 0) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Cannot reset stuck request jobs. No request jobs found.',
        { returnError: true }
      );
    }

    const { componentInstance, result } = this.ModalService.open(
      ConfirmModalComponent,
      { centered: true, size: 'md' }
    );

    Object.assign(componentInstance, {
      displayText:
        'This will clear any actively running Business Verification jobs. Are you sure you wish to continue?'
    });

    from(result).subscribe({
      next: (confirm) => {
        if (confirm) {
          const requestJobsToSave = this.RequestJobs.map((job) => {
            return this.RequestJobService.saveRequestJob(
              Object.assign(job, { status: 'REJECTED' })
            );
          });

          forkJoin(requestJobsToSave).subscribe({
            next: () => {
              this.ToastService.addSuccessToast(
                'Success',
                'Successfully cleared running jobs.'
              );
              this.getProcessingRequestJobs();
              this.cdr.markForCheck();
            }
          });
        }
      }
    });
  }

  overrideComplianceFindings() {
    if (!this.Company || !this.ClientRegisteredVendor) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Cannot override compliance findings. Missing required Client/Vendor info.',
        { returnError: true }
      );
    }

    const { id: CompanyId } = this.Company;
    const { id: ClientRegisteredVendorId } = this.ClientRegisteredVendor;

    const NoteModal = this.ModalService.open(NoteModalComponent, {
      backdrop: 'static',
      modalDialogClass: 'note-modal'
    });
    const { componentInstance } = NoteModal;
    Object.assign(componentInstance, { CompanyId, ClientRegisteredVendorId });

    if (this.AuthService.isClient()) {
      Object.assign(componentInstance, {
        disableTitle: true,
        allowFileUpload: true,
        Note: new Note({ title: 'Business Verification Override Details' })
      });
    }

    if (!this.BusinessComplianceSetting) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Missing compliance settings.',
        { returnError: true }
      );
    }

    if (!this.ComplianceFinding) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Missing compliance findings.',
        { returnError: true }
      );
    }

    from(NoteModal.result).subscribe({
      next: ({ Note, Files }: { Note: Note; Files: FileModel[] }) => {
        const ComplianceFinding = this.ComplianceFindingForm.value;

        // Remove everything that wasn't changed.
        Object.keys(ComplianceFinding).forEach((field) => {
          if (
            !new Set(['id', 'ClientRegisteredVendorId']).has(field) &&
            ComplianceFinding[field as keyof typeof ComplianceFinding] ===
              this.ComplianceFinding![field as keyof ComplianceFinding]
          ) {
            delete ComplianceFinding[field as keyof typeof ComplianceFinding];
          }
        });

        const CompanyVendorFiles = Files.map(
          (File) =>
            new CompanyVendorFile({
              CompanyId,
              ClientRegisteredVendorId,
              docType: 'BusinessVerificationOverride',
              File
            })
        );

        this.ComplianceService.overrideComplianceFinding(
          { ClientRegisteredVendorId },
          { ComplianceFinding, Note, CompanyVendorFiles }
        ).subscribe({
          next: ({ Note, ComplianceFinding, CompanyVendorFiles }) => {
            this.ComplianceFinding = ComplianceFinding;
            this.ComplianceFindingForm.patchValue(ComplianceFinding);

            this.loadCompanyVendorData();

            this.ToastService.addSuccessToast(
              'Success!',
              'Successfully overrided Compliance Findings.'
            );
            this.cdr.markForCheck();
          }
        });
      }
    });
  }

  loadCompanyVendorData() {
    this.Route.paramMap.subscribe({
      next: (paramMap) => {
        const CompanyId = Number(paramMap.get('CompanyId'));
        const ClientRegisteredVendorId = Number(
          paramMap.get('ClientRegisteredVendorId')
        );

        if (!CompanyId) {
          throw this.ToastService.addErrorToast(
            'Error',
            'Cannot retrieve CompanyVendor. Missing Company info.',
            { returnError: true }
          );
        }

        if (!ClientRegisteredVendorId) {
          throw this.ToastService.addErrorToast(
            'Error',
            'Cannot retrieve CompanyVendor. Missing ClientRegisteredVendor info.',
            { returnError: true }
          );
        }

        forkJoin([
          this.CompanyVendorService.getCompanyVendor(
            this.UtilityService.stringifyObjectsInQueryObject({
              attributes: [
                'id',
                'viveStatus',
                'viveSubStatus',
                'isUsingNewBusinessVerification',
                'CompanyId',
                'CompanyComplianceTypeId'
              ],
              CompanyId,
              ClientRegisteredVendorId,
              Company: {
                excludedAttributes: ['logo']
              },
              ClientRegisteredVendor: {
                aliasedAttributes: [
                  'lastRanBusinessVerificationReportDate',
                  'hasBusinessReportData'
                ],
                ComplianceFinding: {}
              }
            })
          ),
          this.ComplianceService.getBusinessComplianceResults(
            CompanyId,
            ClientRegisteredVendorId
          ),
          this.BusinessVerificationApiErrorService.getBusinessVerificationErrors(
            this.UtilityService.stringifyObjectsInQueryObject({
              ClientRegisteredVendorId,
              isArchived: false
            })
          ),
          this.BusinessVerificationApiErrorService.getBusinessVerificationErrors(
            this.UtilityService.stringifyObjectsInQueryObject({
              ClientRegisteredVendorId,
              isArchived: true,
              apiType: 'Lexis Nexis Corporate Filing'
            })
          )
        ]).subscribe({
          next: ([
            CompanyVendor,
            BusinessComplianceResults,
            BusinessVerificationApiErrors,
            ArchivedCorporateFilingErrors
          ]) => {
            this.CompanyVendor = CompanyVendor;
            this.BusinessComplianceResults = BusinessComplianceResults;
            this.BusinessVerificationApiErrors = [
              ...BusinessVerificationApiErrors,
              ...ArchivedCorporateFilingErrors
            ];

            this.companyVendorChanged.emit(this.CompanyVendor);

            if (!this.BusinessComplianceResults) {
              throw this.ToastService.addErrorToast(
                'Error',
                'Missing compliance results.',
                { returnError: true }
              );
            }

            const { UserAccountType, isSuperUser } = this.LoggedInUser;

            if (!UserAccountType) {
              throw this.ToastService.addErrorToast(
                'Error',
                'Missing User Account type.',
                { returnError: true }
              );
            }

            const { name } = UserAccountType;

            // For the Client, disable fields that aren't failed/missing.
            if (name === 'Client') {
              if (isSuperUser) {
                Object.keys(this.ComplianceResultsToFindingsMap).forEach(
                  (result) => {
                    const findingField =
                      this.ComplianceResultsToFindingsMap[
                        result as keyof ComplianceResultsToFindingsMap
                      ];

                    if (
                      this.BusinessComplianceResults &&
                      !new Set([
                        'FAILED',
                        'NO EXACT MATCH',
                        'REVIEW NEEDED',
                        'MISSING'
                      ]).has(
                        this.BusinessComplianceResults[
                          result as keyof ComplianceResultsToFindingsMap
                        ]
                      )
                    ) {
                      this.ComplianceFindingForm.controls[
                        findingField
                      ].disable();
                    }
                  }
                );
              } else {
                this.ComplianceFindingForm.disable();
              }
            }

            const errorsWithNextRunDate =
              this.BusinessVerificationApiErrors.filter(
                (error) => error.nextRunDate != null
              );

            if (errorsWithNextRunDate.length > 0) {
              this.BusinessVerificationApiErrorLongestRetryTime =
                errorsWithNextRunDate.reduce((latest, current) => {
                  const latestDate = new Date(latest.nextRunDate!);
                  const currentDate = new Date(current.nextRunDate!);
                  return currentDate > latestDate ? current : latest;
                });
            }

            const {
              Company,
              ClientRegisteredVendor,
              CompanyId,
              CompanyComplianceTypeId
            } = this.CompanyVendor;

            if (Company) {
              this.Company = Company;
            }

            if (CompanyId && CompanyComplianceTypeId) {
              this.ComplianceService.getBusinessComplianceSetting(
                this.UtilityService.stringifyObjectsInQueryObject({
                  CompanyId,
                  CompanyComplianceTypeId
                })
              ).subscribe({
                next: (
                  BusinessComplianceSetting: BusinessComplianceSetting
                ) => {
                  if (!BusinessComplianceSetting) {
                    throw this.ToastService.addErrorToast(
                      'Error',
                      'There was an error retrieving BusinessCompliance setting.',
                      { returnError: true }
                    );
                  }

                  this.BusinessComplianceSetting = BusinessComplianceSetting;

                  this.BusinessComplianceSettingForm.patchValue(
                    BusinessComplianceSetting
                  );
                  this.cdr.markForCheck();
                }
              });
            }

            if (ClientRegisteredVendor) {
              this.ClientRegisteredVendor = ClientRegisteredVendor;
              this.getProcessingRequestJobs();

              const { ComplianceFinding, hasBusinessReportData } =
                this.ClientRegisteredVendor;

              if (hasBusinessReportData) {
                this.hasBusinessReportData = hasBusinessReportData;
              }

              if (ComplianceFinding) {
                this.ComplianceFinding = ComplianceFinding;
                this.ComplianceFindingForm.patchValue(ComplianceFinding);
              }
            }
            this.cdr.markForCheck();
          }
        });
      }
    });
  }

  ngOnInit(): void {
    this.loadCompanyVendorData();
  }

  ngOnDestroy(): void {
    if (this.RequestJobTimer) {
      clearInterval(this.RequestJobTimer);
    }
  }
}
